import React, { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button, Modal, Form, Row, Col } from "react-bootstrap";
import { setDeliveryInfo as setDeliveryInfoState, setOrderDeliveryInfo } from "state/orderEdition/actions";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPen, faCopy } from '@fortawesome/free-solid-svg-icons'
import Select from "react-select";
import swal from "sweetalert";
import Tippy from '@tippyjs/react';
import 'tippy.js/dist/tippy.css';
import AddressAutocompleteInput from "./AddressAutocompleteInput";

const DeliveryInfoComponent = ({ title, orderIndex }) => {

  const dispatch = useDispatch();
  const [show, setShow] = useState(false);
  const addresses = useSelector((state) => state?.adminData?.addresses || []);

  const orders = useSelector((state) => state.orderEdition.orders);
  const order = useSelector((state) => {
    if (orderIndex === undefined) {
      return null;
    } else {
      return state.orderEdition.orders[orderIndex];
    }
  });
  const [deliveryInformationSpecific, setDeliveryInformationSpecific] = useState(order?.deliveryInfo?.deliverySpecific || false);

  const deliveryInfoState = useSelector((state) => {
    if (orderIndex === undefined) {
      return state.orderEdition.deliveryInfo;
    } else {
      return state.orderEdition.orders[orderIndex]?.deliveryInfo;
    }
  });

  const billingInfoState = useSelector(
    (state) => state.orderEdition.billingInfo
  );

  // Livraison générale du devis : source de la copie pour une livraison spécifique de prestation
  // (demande de Julie, Trello Iu2tR1HG). Le bloc général, lui, copie toujours la facturation.
  const devisDeliveryInfoState = useSelector(
    (state) => state.orderEdition.deliveryInfo
  );
  const isPrestationBlock = orderIndex !== undefined;

  const [deliveryInfo, setDeliveryInfo] = useState({
    lastname: deliveryInfoState?.lastname || "",
    firstname: deliveryInfoState?.firstname || "",
    company: deliveryInfoState?.company || "",
    phone: deliveryInfoState?.phone || "",
    phoneSecondary: deliveryInfoState?.phoneSecondary || "",
    streetNumber: deliveryInfoState?.streetNumber || "",
    street: deliveryInfoState?.street || "",
    postalCode: deliveryInfoState?.postalCode || "",
    city: deliveryInfoState?.city || "",
    addressComplement: deliveryInfoState?.addressComplement || "",
    deliveryAddressBook: deliveryInfoState?.deliveryAddressBook || null,
  });

  const [deliveryInformation, setDeliveryInformation] = useState(deliveryInfoState?.deliveryInformation || null);
  const [recoverInformation, setRecoverInformation] = useState(deliveryInfoState?.recoverInformation || null);
  const [addressesFormat, setAddressesFormat] = useState([]);
  const [optionSelected, setSelectedOptions] = useState(null);

  const formatPreview = useCallback((address) => {
    return [
      address?.street_number,
      address?.street,
      address?.postal_code,
      address?.city,
    ]
      .filter(Boolean)
      .join(" ")
      .replace(/\s+/g, " ")
      .trim();
  }, []);

  const buildAddressOption = useCallback(
    (address) => {
      const preview = formatPreview(address);
      return {
        value: address.id,
        label: [address.name_address, preview].filter(Boolean).join(" - "),
        preview,
        ...address,
      };
    },
    [formatPreview]
  );

  const filterAddressOption = (option, rawInput) => {
    if (!rawInput) {
      return true;
    }
    const input = rawInput.toLowerCase();
    const data = option.data || option;
    return [
      option.label,
      data.name_address,
      data.preview,
      data.street,
      data.street_number,
      data.postal_code,
      data.city,
    ]
      .filter(Boolean)
      .some((value) => value.toString().toLowerCase().includes(input));
  };

  const formatAddressOptionLabel = (option) => (
    <div>
      <div>{option.name_address || option.label}</div>
      <small className="text-muted">
        {option.preview || formatPreview(option) || "Adresse inconnue"}
      </small>
    </div>
  );

  useEffect(() => {
    setDeliveryInformationSpecific(order?.deliveryInfo?.deliverySpecific || false);
    setDeliveryInfo({
      lastname: deliveryInfoState?.lastname || "",
      firstname: deliveryInfoState?.firstname || "",
      company: deliveryInfoState?.company || "",
      phone: deliveryInfoState?.phone || "",
      phoneSecondary: deliveryInfoState?.phoneSecondary || "",
      streetNumber: deliveryInfoState?.streetNumber || "",
      street: deliveryInfoState?.street || "",
      postalCode: deliveryInfoState?.postalCode || "",
      city: deliveryInfoState?.city || "",
      addressComplement: deliveryInfoState?.addressComplement || "",
      deliveryAddressBook: deliveryInfoState?.deliveryAddressBook || null,
    });
  }, [order, deliveryInfoState]);

  const handleSelectAddressChange = (event) => {
    
    setSelectedOptions(event);
    
    const selectedAddress = addresses.find(
      (adresse) => adresse.id === Number(event.value)
    );
    if (selectedAddress) {
      setDeliveryInfo({
        ...deliveryInfo,
        streetNumber: selectedAddress?.street_number || "",
        street: selectedAddress?.street || "",
        postalCode: selectedAddress?.postal_code || "",
        city: selectedAddress?.city || "",
        addressComplement: selectedAddress?.address_complement || "",
        deliveryAddressBook: selectedAddress
      });
      setDeliveryInformation(selectedAddress?.delivery_information || "");
      setRecoverInformation(selectedAddress?.recover_information || "");
    }
  };

  const handleDeliveryInformationSpecific = (e) => {
    setDeliveryInformationSpecific(e.target.checked);
    const deliveryInfo = order.deliveryInfo;
    // En cochant, un bloc encore vide part des infos de livraison du devis (à ajuster ensuite)
    const isEmpty = !deliveryInfo?.lastname && !deliveryInfo?.firstname && !deliveryInfo?.company
      && !deliveryInfo?.street && !deliveryInfo?.city;
    dispatch(
      setOrderDeliveryInfo(orderIndex, {
        ...deliveryInfo,
        ...(e.target.checked && isEmpty ? deliveryFieldsFrom(devisDeliveryInfoState) : {}),
        deliverySpecific: e.target.checked
      })
    );
  }

  // Champs d'adresse de livraison repris d'une autre livraison (celle du devis)
  const deliveryFieldsFrom = (source) => ({
    lastname: source?.lastname || "",
    firstname: source?.firstname || "",
    company: source?.company || "",
    phone: source?.phone || "",
    phoneSecondary: source?.phoneSecondary || "",
    streetNumber: source?.streetNumber || "",
    street: source?.street || "",
    postalCode: source?.postalCode || "",
    city: source?.city || "",
    addressComplement: source?.addressComplement || "",
    deliveryAddressBook: source?.deliveryAddressBook || null,
  });

  const handleClose = () => setShow(false);
  const handleShow = () => setShow(true);

  const handleChange = (e) => {
    setDeliveryInfo({
      ...deliveryInfo,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = (e) => {
    if (e) {
      e.preventDefault();
    }
    if (orderIndex === undefined) {
      // Les infos de livraisons
      dispatch(
        setDeliveryInfoState({
          ...deliveryInfoState,
          ...deliveryInfo
        })
      );
      
      // Les commentaire de livraison et de récupération
      if(deliveryInformation || recoverInformation){
        swal({
          title: "Pré-remplir les commentaires livraison/récup du carnet d'adresse ? ",
          icon: "info",
          buttons: ["Non", "Oui"]
        }).then((confirm) => {
          if (confirm) {
            orders.forEach((o, oi) => {
              if (!o.deliveryInfo.deliverySpecific) {
                dispatch(setOrderDeliveryInfo(oi, { ...o.deliveryInfo, deliveryInformation, recoverInformation }));
              }
            });
          }
        });
      }
    } else {
      if(deliveryInformation || recoverInformation){
        swal({
          title: "Pré-remplir les commentaires livraison/récup du carnet d'adresse ? ",
          icon: "info",
          buttons: ["Non", "Oui"]
        }).then((confirm) => {
          if (confirm) {
            dispatch(setOrderDeliveryInfo(orderIndex, { ...order.deliveryInfo, ...deliveryInfo, deliveryInformation, recoverInformation }));
          }
          else{
            dispatch(setOrderDeliveryInfo(orderIndex, { ...deliveryInfoState, ...deliveryInfo }));
          }
        });
      }
      else{
        dispatch(setOrderDeliveryInfo(orderIndex, { ...deliveryInfoState, ...deliveryInfo }));
      }
    }
    handleClose();
  };

  useEffect(() => {
    const formatted = addresses.map(buildAddressOption);
    setAddressesFormat(formatted);
    const selectedId = deliveryInfo.deliveryAddressBook?.id;
    setSelectedOptions(formatted.find((item) => item.value === selectedId) || null);
  }, [addresses, deliveryInfo.deliveryAddressBook?.id, buildAddressOption]);

  useEffect(() => {
    setSelectedOptions(addressesFormat.find((item) => item.value === deliveryInfo.deliveryAddressBook?.id) || null);
    setDeliveryInformation(deliveryInfo.deliveryAddressBook?.delivery_information || "");
    setRecoverInformation(deliveryInfo.deliveryAddressBook?.recover_information || "");
  }, [deliveryInfo.deliveryAddressBook?.id, addressesFormat]);
  
  // Bloc de prestation (livraison spécifique) : copie la livraison du devis, pas la facturation
  const copyDevisDeliveryInfo = () => {
    const newDeliveryInfo = { ...deliveryInfo, ...deliveryFieldsFrom(devisDeliveryInfoState) };
    setDeliveryInfo(newDeliveryInfo);
    dispatch(
      setOrderDeliveryInfo(orderIndex, {
        ...deliveryInfoState,
        ...newDeliveryInfo
      })
    );
  };

  const copyBillingInfo = () => {
    const matchedAddress = addresses.find(
      (address) =>
        address.street_number === billingInfoState?.streetNumber &&
        address.street === billingInfoState?.street &&
        address.postal_code === billingInfoState?.postalCode &&
        address.city === billingInfoState?.city
    );

    const newDeliveryInfo = {
      ...deliveryInfo,
      lastname: billingInfoState?.lastname || "",
      firstname: billingInfoState?.firstname || "",
      company: billingInfoState?.company || "",
      phone: billingInfoState?.phone || "",
      streetNumber: billingInfoState?.streetNumber || "",
      street: billingInfoState?.street || "",
      postalCode: billingInfoState?.postalCode || "",
      city: billingInfoState?.city || "",
      addressComplement: billingInfoState?.addressComplement || "",
      deliveryAddressBook: matchedAddress || deliveryInfo.deliveryAddressBook,
    };
    
    setDeliveryInfo(newDeliveryInfo);
    
    if (orderIndex === undefined) {
      dispatch(
        setDeliveryInfoState({
          ...deliveryInfoState,
          ...newDeliveryInfo
        })
      );
    } else {
      dispatch(
        setOrderDeliveryInfo(orderIndex, {
          ...deliveryInfoState,
          ...newDeliveryInfo
        })
      );
    }
  };

  return (
    <>
      {orderIndex !== undefined && <h5><input type="checkbox" id="deliveryInformationSpecific" name="deliveryInformationSpecific" required onChange={handleDeliveryInformationSpecific} checked={deliveryInformationSpecific} /> Information de livraison spécifique</h5>}
      {(deliveryInformationSpecific || orderIndex === undefined) && (
        <>
          <Modal show={show} onHide={handleSubmit}>
            <Modal.Header closeButton>
              <Modal.Title>Informations de livraison</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <Form onSubmit={handleSubmit}>
                <Form.Group>
                  <Form.Label>Carnet d'adresses</Form.Label>
                  <Select
                    options={addressesFormat}
                    closeMenuOnSelect={true}
                    onChange={handleSelectAddressChange}
                    value={optionSelected}
                    name={"address"}
                    formatOptionLabel={formatAddressOptionLabel}
                    filterOption={filterAddressOption}
                  />
                </Form.Group>
                <Form.Group>
                  <Form.Label>Nom</Form.Label>
                  <Form.Control
                    type="text"
                    name="lastname"
                    value={deliveryInfo.lastname}
                    onChange={handleChange}
                  />
                </Form.Group>
                <Form.Group>
                  <Form.Label>Prénom</Form.Label>
                  <Form.Control
                    type="text"
                    name="firstname"
                    value={deliveryInfo.firstname}
                    onChange={handleChange}
                  />
                </Form.Group>
                <Form.Group>
                  <Form.Label>Entreprise</Form.Label>
                  <Form.Control
                    type="text"
                    name="company"
                    value={deliveryInfo.company}
                    onChange={handleChange}
                  />
                </Form.Group>
                <Form.Group>
                  <Form.Label>Téléphone</Form.Label>
                  <Form.Control
                    type="text"
                    name="phone"
                    value={deliveryInfo.phone}
                    onChange={handleChange}
                  />
                </Form.Group>
                <Form.Group>
                  <Form.Label>Téléphone secondaire</Form.Label>
                  <Form.Control
                    type="text"
                    name="phoneSecondary"
                    value={deliveryInfo.phoneSecondary}
                    onChange={handleChange}
                  />
                </Form.Group>
                <Form.Group>
                  <Form.Label>Recherche d'adresse (remplit les champs ci-dessous)</Form.Label>
                  <AddressAutocompleteInput
                    onSelect={(address) =>
                      setDeliveryInfo({
                        ...deliveryInfo,
                        streetNumber: address.streetNumber,
                        street: address.street,
                        postalCode: address.postalCode,
                        city: address.city,
                      })
                    }
                  />
                </Form.Group>
                <Form.Group>
                  <Form.Label>Numéro de rue</Form.Label>
                  <Form.Control
                    type="text"
                    name="streetNumber"
                    value={deliveryInfo.streetNumber}
                    onChange={handleChange}
                  />
                </Form.Group>
                <Form.Group>
                  <Form.Label>Rue</Form.Label>
                  <Form.Control
                    type="text"
                    name="street"
                    value={deliveryInfo.street}
                    onChange={handleChange}
                  />
                </Form.Group>
                <Form.Group>
                  <Form.Label>Code Postal</Form.Label>
                  <Form.Control
                    type="text"
                    name="postalCode"
                    value={deliveryInfo.postalCode}
                    onChange={handleChange}
                  />
                </Form.Group>
                <Form.Group>
                  <Form.Label>Ville</Form.Label>
                  <Form.Control
                    type="text"
                    name="city"
                    value={deliveryInfo.city}
                    onChange={handleChange}
                  />
                </Form.Group>
                <Form.Group>
                  <Form.Label>Complément d'adresse</Form.Label>
                  <Form.Control
                    type="text"
                    name="addressComplement"
                    value={deliveryInfo.addressComplement}
                    onChange={handleChange}
                  />
                </Form.Group>
                <Button variant="primary" type="submit">
                  Enregistrer
                </Button>
              </Form>
            </Modal.Body>
          </Modal>

          <>
            <Row>
              <Col xs={7} sm={7} md={7}>
                <h5>{title}</h5>
              </Col>
              <Col xs={5} sm={5} md={5} className="text-right">
                <Tippy content={isPrestationBlock ? "Copier la livraison du devis" : "Copier info facturation sur livraison"}>
                  <Button variant="success" size="sm" className="mr-2" onClick={isPrestationBlock ? copyDevisDeliveryInfo : copyBillingInfo}>
                    <FontAwesomeIcon icon={faCopy} />
                  </Button>
                </Tippy>
                <Button variant="success" size="sm" onClick={handleShow}>
                  <FontAwesomeIcon icon={faPen} />
                </Button>
              </Col>
            </Row>
            
            <p>
              {deliveryInfo.lastname || "Nom"}{" "}
              {deliveryInfo.firstname || "Prénom"}
              <br />
              {deliveryInfo.company || "Entreprise"}
              <br />
              {deliveryInfo.phone || "Tel."}
              <br />
              {deliveryInfo.phoneSecondary || "Tel. sec."}
              <br />
              {deliveryInfo.streetNumber || "Num."} {deliveryInfo.street || "Rue"}
              <br />
              {deliveryInfo.postalCode || "Code postal"}{" "}
              {deliveryInfo.city || "Ville"}
              <br />
              {deliveryInfo.addressComplement || "Complém."}
            </p>
          </>
        </>
      )}
    </>
  );
};

export default DeliveryInfoComponent;
